import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as _ from 'lodash';
import * as React from 'react';
import {Button, Icon} from 'semantic-ui-react';
import styled from 'styled-components';

import {ChartAggOption} from '../generated/graphql';
import {
  configKeysInExpression,
  escapeIdentifier,
  type Expression,
  parseFancyExpression,
  summaryKeysInExpression,
} from '../util/expr';
import {isSystemMetric} from '../util/metrics';
import {isReservedKey} from '../util/runs';
import {PanelExpressionInput} from './PanelExpressionInput';
import {type RunsLinePlotConfig} from './PanelRunsLinePlot/types';
import {SettingLabel} from './WorkspaceDrawer/Settings/components/SettingLabel';

type PanelConfig = RunsLinePlotConfig;

interface PanelLegendProps {
  type: 'lines' | 'bars' | 'scalar';
  config: PanelConfig;
  updateConfig: (newConfig: PanelConfig) => void;
  availableExpressionVarNames: string[];
  exampleIdentifier: string;
}

export function getExpressionFields(parsedExpressions: {
  expressions?: Expression[];
  xExpression?: Expression;
}) {
  const expressionSummaryFields = _.concat(
    parsedExpressions.expressions || [],
    parsedExpressions.xExpression || []
  ).flatMap(expr => summaryKeysInExpression(expr));

  const expressionConfigFields = _.concat(
    parsedExpressions.expressions || [],
    parsedExpressions.xExpression || []
  ).flatMap(expr => configKeysInExpression(expr));

  const expressionFields = _.concat(
    expressionConfigFields,
    expressionSummaryFields
  );
  return expressionFields;
}

export function parseExpressions(expressions?: string[], xExpression?: string) {
  let expr: Expression[] | undefined;
  let xExpr: Expression | undefined;
  if (expressions != null) {
    expr = expressions
      .map(e => parseFancyExpression(e))
      .filter(e => e != null) as Expression[];
  }
  if (xExpression != null) {
    xExpr = parseFancyExpression(xExpression);
  }
  return {expressions: expr, xExpression: xExpr};
}

export function renderSingleExpressionInput(
  config: PanelConfig,
  updateConfig: (newConfig: PanelConfig) => void,
  placeholder: string
) {
  return renderExpressionInput(
    config.expressions != null && config.expressions.length > 0
      ? config.expressions[0]
      : undefined,
    value => {
      updateConfig({expressions: [value]});
    },

    placeholder
  );
}

export function renderXExpressionInput(
  config: PanelConfig,
  updateConfig: (newConfig: PanelConfig) => void,
  availableExpressionVarNames: string[]
) {
  return (
    <Tailwind>
      <SettingLabel text="X Axis Expression" />
      {renderExpressionInput(
        config.xExpression,
        value => {
          updateConfig({xExpression: value});
        },
        config.xAxis
      )}
      {renderExampleVariables(availableExpressionVarNames)}
    </Tailwind>
  );
}

export function renderExpressionInput(
  expression: string | undefined,
  updateExpression: (value: string) => void,
  placeholder?: string
) {
  return (
    <PanelExpressionInput
      expression={expression ?? ''}
      placeholder={escapeIdentifier(placeholder ?? '')}
      onChange={updateExpression}
    />
  );
}
export function renderExampleVariables(availableExpressionVarNames: string[]) {
  return (
    <Tailwind>
      <div className="mt-16">
        <SettingLabel text="Useful Variables:" />
        <div className="mb-6">
          <span className="mr-4 inline-block">Simple metrics:</span>
          <>
            {availableExpressionVarNames
              .filter(
                varName =>
                  !varName.startsWith('system') && !isReservedKey(varName)
              )
              .slice(0, 3)
              .map((varName, index, arr) => (
                // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                <span key={index}>
                  <pre className="inline-block p-2 font-mono text-sm">
                    {escapeIdentifier(varName)}
                  </pre>
                  {index < arr.length - 1 && <span>, </span>}
                </span>
              ))}
          </>
        </div>
        <div className="mb-6">
          <span className="mr-4 inline-block">
            Complex metrics (wrap in
            <pre className="inline-block p-2 font-mono text-sm">{'${}'}</pre>):
          </span>
          <pre className="inline-block p-2 font-mono text-sm">
            {'${metric:mean}'}
          </pre>
          ,{' '}
          <pre className="inline-block p-2 font-mono text-sm">
            {'${config:lr}'}
          </pre>
        </div>
      </div>
    </Tailwind>
  );
}

const StyledHeader = styled.div`
  font-weight: 600;
  margin-top: 12px;
`;

export const PanelExpressionOptionsOutliers = ({
  config,
  updateConfig,
  availableExpressionVarNames,
  type,
}: PanelLegendProps) => {
  if (type === 'scalar') {
    return null;
  }

  return (
    <>
      {type === 'lines' &&
        renderXExpressionInput(
          config,
          updateConfig,
          availableExpressionVarNames.filter(n => !isSystemMetric(n))
        )}
    </>
  );
};

export const PanelExpressionOptions = (props: PanelLegendProps) => {
  const {config, updateConfig, availableExpressionVarNames} = props;

  const escapedExampleIdentifier = escapeIdentifier(
    config.metrics != null && config.metrics.length > 0
      ? props.exampleIdentifier
      : 'x'
  );

  if (props.type === 'scalar') {
    return (
      <>
        <StyledHeader>Expressions</StyledHeader>
        <p className="hint-text">
          Add calculated metrics to your graph. For example 1 -{' '}
          {escapedExampleIdentifier}.
        </p>
        {renderSingleExpressionInput(
          config,
          updateConfig,
          props.exampleIdentifier
        )}
      </>
    );
  }

  return (
    <>
      <StyledHeader>Expressions</StyledHeader>
      <p className="hint-text">
        Add calculated metrics to your graph. For example 1 -{' '}
        {escapedExampleIdentifier}.
      </p>

      <>
        {(config.expressions || []).map((expression, i) => (
          <PanelExpressionInput
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={`expression-${i}`}
            expression={expression}
            placeholder={escapedExampleIdentifier}
            // oxlint-disable-next-line wandb/jsx-no-new-function-as-prop -- PanelExpressionInput not React.memo'd and handler is in .map (no hooks); to optimize: memoize the child + lift the row handler
            onChange={value => {
              const newExpressions =
                config.expressions != null ? [...config.expressions] : [];
              if (i >= newExpressions.length) {
                newExpressions.push(value);
              } else {
                newExpressions[i] = value;
              }

              updateConfig({
                expressions: newExpressions,
                groupArea: 'none',
                groupAgg: ChartAggOption.Mean,
              });
            }}
            // oxlint-disable-next-line wandb/jsx-no-new-function-as-prop -- PanelExpressionInput not React.memo'd and handler is in .map (no hooks); to optimize: memoize the child + lift the row handler
            onRemove={() => {
              if (config.expressions != null && i < config.expressions.length) {
                const newExpressions =
                  config.expressions != null ? [...config.expressions] : [];
                newExpressions.splice(i, 1);
                updateConfig({
                  expressions: newExpressions,
                });
              }
            }}
          />
        ))}
        <Button
          onClick={() => {
            const newExpressions = [...(config.expressions ?? []), ''];
            updateConfig({
              expressions: newExpressions,
            });
          }}
          className="expression-add">
          <Icon title="" name="plus" />
          Add an Expression
        </Button>
      </>

      <br />

      {props.type === 'lines' &&
        renderXExpressionInput(
          config,
          updateConfig,
          availableExpressionVarNames
        )}
    </>
  );
};
